import { roomLib } from "@agentcall/core/lib"
import { useCallback, useEffect, useMemo, useState } from "react"
import { Briefcase, Plus, Search } from "react-feather"
import { useLocation, useNavigate, useSearchParams } from "react-router"
import { propertyApi } from "@/api/client/property"
import { EchoesEditGuard } from "@/components/echoes/EchoesEditGuard"
import { Button } from "@/components/ui/button"
import { EmptyCard, PropertyListCard } from "@/components/ui/card"
import { CustomPagination } from "@/components/ui/custom-pagination"
import { ReactErrorBoundary } from "@/components/ui/error-boundary/ReactErrorBoundary"
import { Input } from "@/components/ui/input"
import { Loading } from "@/components/ui/loading"
import { useIsEchoesOwner } from "@/store/session"

// Main content component that uses Suspense
function PropertiesPageContent() {
  const [searchInput, setSearchInput] = useState("")
  const [searchTerm, setSearchTerm] = useState("")
  const [searchParams, setSearchParams] = useSearchParams()
  const location = useLocation()

  const navigate = useNavigate()
  const isEchoesOwner = useIsEchoesOwner()
  const ITEMS_PER_PAGE = 10

  // URLパラメータからページ番号を取得（デフォルトは1）
  const currentPage = Number(searchParams.get("page")) || 1

  const {
    data,
    refetch,
    error,
    isLoading: isPropertyLoading,
  } = propertyApi.usePropertyList(
    {
      limit: 1000, // 全データを取得
      withRooms: "true",
      withRoomsPlans: "true",
    },
    undefined
  )

  // エラーがある場合はthrowして、ErrorBoundaryにキャッチさせる
  if (error) {
    throw error
  }

  // navigation stateからrefetchフラグをチェック
  useEffect(() => {
    if (location.state?.shouldRefetch) {
      refetch()
      // stateをクリア
      // navigate(location.pathname + location.search, { replace: true })
    }
  }, [location.state, refetch])

  const allProperties = data?.properties || []

  const buildAddress = useCallback(
    (property: (typeof allProperties)[number]) =>
      `${property.prefecture ?? ""}${property.city ?? ""}${property.town ?? ""}${property.district ?? ""}`,
    []
  )

  // 検索フィルタリング
  const filteredProperties = useMemo(() => {
    if (!searchTerm) return allProperties

    const lowerSearchTerm = searchTerm.toLowerCase()
    return allProperties.filter((property) => {
      // 物件名で検索
      if (property.name?.toLowerCase().includes(lowerSearchTerm)) return true
      // 住所で検索
      if (buildAddress(property).toLowerCase().includes(lowerSearchTerm)) return true
      // 部屋名で検索
      if (property.rooms?.some((room) => room.name?.toLowerCase().includes(lowerSearchTerm)))
        return true
      return false
    })
    // buildAddress is stable (pure) but include to satisfy exhaustive-deps
  }, [allProperties, searchTerm, buildAddress])

  // ページング計算
  const totalPages = Math.ceil(filteredProperties.length / ITEMS_PER_PAGE)
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE
  const endIndex = startIndex + ITEMS_PER_PAGE
  const currentProperties = filteredProperties.slice(startIndex, endIndex)

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchTerm(searchInput)
      // 検索時はページを1にリセット
      if (searchInput !== searchTerm) {
        setSearchParams({ page: "1" })
      }
    }, 500)

    return () => clearTimeout(timer)
  }, [searchInput, searchTerm, setSearchParams])

  // ページ番号が範囲外の場合は1ページ目にリダイレクト
  useEffect(() => {
    if (totalPages > 0 && (currentPage < 1 || currentPage > totalPages)) {
      setSearchParams({ page: "1" })
    }
  }, [currentPage, totalPages, setSearchParams])

  const handlePageChange = (page: number) => {
    setSearchParams({ page: page.toString() })
    // ページトップにスクロール
    window.scrollTo({ top: 0, behavior: "smooth" })
  }

  const handleEditProperty = (propertyId: number) => {
    navigate(`/properties/${propertyId}/edit`, { state: { from: "list" } })
  }

  const handleViewDetails = (propertyId: number) => {
    navigate(`/properties/${propertyId}`)
  }

  return (
    <>
      {/* Search Input */}
      <Input
        placeholder="物件名や部屋名で検索"
        value={searchInput}
        onChange={(e) => setSearchInput(e.target.value)}
        startIcon={<Search className="h-4 w-4 text-slate-300" />}
        className="w-full mt-5"
      />
      {/* Properties List */}
      <div className="space-y-2.5 mt-7">
        {/* Property Count */}
        <div className="text-base font-semibold text-primary tracking-tight leading-[17px]">
          {!isPropertyLoading &&
            `${filteredProperties.length}件${searchTerm ? ` ("${searchTerm}"の検索結果)` : ""}`}
        </div>

        {isPropertyLoading && <Loading className="py-8" text="物件情報を読み込んでいます..." />}

        {/* Property Cards or Empty State */}
        {!isPropertyLoading &&
          (currentProperties.length > 0 ? (
            currentProperties.map((property) => (
              <PropertyListCard
                key={property.id}
                title={property.name}
                address={buildAddress(property)}
                stats={[
                  { label: "登録戸数", value: (property.rooms?.length || 0).toString() },
                  {
                    label: "サービス利用数",
                    value: (
                      property.rooms?.filter((room) => roomLib.roomCurrentPlan(room)).length || 0
                    ).toString(),
                  },
                ]}
                buttonText="詳細を見る"
                // Echoes連携オーナーは物件編集不可(E-08)のため編集ボタンを非表示にする
                onEdit={isEchoesOwner ? undefined : () => handleEditProperty(property.id)}
                onButtonClick={() => handleViewDetails(property.id)}
              />
            ))
          ) : (
            <EmptyCard text="検索結果がありません" />
          ))}
      </div>

      {/* Pagination */}
      {!isPropertyLoading && (
        <CustomPagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
        />
      )}
    </>
  )
}

// Wrapper component with Suspense
export function PropertiesPage() {
  const navigate = useNavigate()

  const handleAddProperty = () => {
    navigate("/properties/add")
  }

  return (
    <>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <Briefcase className="h-[18px] w-[18px] text-primary" />
          <h1 className="text-base font-semibold text-primary tracking-tight leading-5">
            物件一覧
          </h1>
        </div>
        {/* Add Property Button */}
        <div className="flex justify-end">
          <EchoesEditGuard>
            <Button onClick={handleAddProperty} className="flex items-center gap-2.5">
              <Plus className="h-3.5 w-3.5" />
              新規登録
            </Button>
          </EchoesEditGuard>
        </div>
      </div>
      <ReactErrorBoundary>
        <PropertiesPageContent />
      </ReactErrorBoundary>
    </>
  )
}
